import React, { useEffect, useState, useMemo } from "react";
import { listEconomicIndicators, getEconomicIndicatorsMeta } from "../../api/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import PageBackButton from "../../components/PageBackButton";
import {
  LineChart, Line, CartesianGrid, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend,
} from "recharts";

const INDICATOR_COLORS = {
  "GDP": "#10b981",
  "CPI": "#f59e0b",
  "Unemployment": "#ef4444",
  "Interest Rate": "#6366f1",
  "Retail Sales": "#0ea5e9",
  "Industrial Production": "#8b5cf6",
};

const CURRENCY_OPTIONS = ["USD", "EUR", "JPY", "GBP", "CAD", "AUD", "CHF", "NZD"];

export default function EconomicIndicatorsPage() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [meta, setMeta] = useState(null);

  // Filters
  const [currency, setCurrency] = useState("USD");
  const [indicator, setIndicator] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Chart state
  const [selectedIndicator, setSelectedIndicator] = useState(null);

  useEffect(() => {
    // Set date range to last 5 years
    const now = new Date();
    const fiveYearsAgo = new Date(now);
    fiveYearsAgo.setFullYear(now.getFullYear() - 5);
    const fmt = (d) => d.toISOString().slice(0, 10);
    setDateFrom(fmt(fiveYearsAgo));
    setDateTo(fmt(now));

    // Fetch metadata and initial data
    fetchMeta();
    fetchData({ currency: "USD", date_from: fmt(fiveYearsAgo), date_to: fmt(now) });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchMeta = async () => {
    try {
      const res = await getEconomicIndicatorsMeta();
      // Interceptor already extracts .data — res is the object directly
      setMeta(res);
    } catch {
      // meta is non-critical
    }
  };

  const fetchData = async (overrides = {}) => {
    try {
      setLoading(true);
      setError(null);
      const params = {
        currency: overrides.currency ?? currency,
        indicator: overrides.indicator ?? (indicator || undefined),
        date_from: overrides.date_from ?? dateFrom,
        date_to: overrides.date_to ?? dateTo,
      };
      const res = await listEconomicIndicators(params);
      // Interceptor already extracts .data — res is the array directly
      const items = Array.isArray(res) ? res : res?.results || [];
      setData(items);
    } catch (e) {
      setError(e.message || "Failed to load economic indicators");
    } finally {
      setLoading(false);
    }
  };

  // Group data by indicator for summary cards
  const groupedByIndicator = useMemo(() => {
    const groups = {};
    for (const item of data) {
      if (!groups[item.indicator]) groups[item.indicator] = [];
      groups[item.indicator].push(item);
    }
    // Sort each group by date descending
    for (const key of Object.keys(groups)) {
      groups[key].sort((a, b) => (a.date > b.date ? -1 : 1));
    }
    return groups;
  }, [data]);

  // Chart data for selected indicator
  const chartData = useMemo(() => {
    if (!selectedIndicator || !groupedByIndicator[selectedIndicator]) return [];
    return [...groupedByIndicator[selectedIndicator]]
      .sort((a, b) => (a.date < b.date ? -1 : 1))
      .map((d) => ({
        date: d.date,
        value: d.value,
      }));
  }, [selectedIndicator, groupedByIndicator]);

  const availableIndicators = meta?.country_indicators
    ? meta.country_indicators[meta.currency_to_country?.[currency]] || []
    : Object.keys(groupedByIndicator);

  return (
    <div className="max-w-7xl mx-auto space-y-4">
      <div className="flex items-start gap-3">
        <PageBackButton label="Back to Research" to="/research" preferHistory={false} hideLabelOnMobile />
        <div>
          <h1 className="text-2xl font-semibold">Economic Indicators</h1>
          <p className="text-sm text-textSecondary">
            Key economic data from FRED — GDP, CPI, unemployment, interest rates, and more
          </p>
        </div>
      </div>

      {/* Filters */}
      <Card className="p-4 space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
          <div>
            <label className="block text-sm mb-2">Currency</label>
            <div className="flex flex-wrap gap-2">
              {CURRENCY_OPTIONS.map((c) => (
                <Button
                  key={c}
                  type="button"
                  variant={currency === c ? "default" : "secondary"}
                  size="sm"
                  onClick={() => setCurrency(c)}
                >
                  {c}
                </Button>
              ))}
            </div>
          </div>
          <div>
            <label className="block text-sm mb-1">Indicator</label>
            <select
              className="w-full rounded-md border border-border bg-secondary text-textPrimary px-3 py-2 text-sm [&>option]:bg-secondary [&>option]:text-textPrimary"
              value={indicator}
              onChange={(e) => setIndicator(e.target.value)}
            >
              <option value="">All Indicators</option>
              {availableIndicators.map((ind) => (
                <option key={ind} value={ind}>{ind}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm mb-1">From</label>
            <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} />
          </div>
          <div>
            <label className="block text-sm mb-1">To</label>
            <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} />
          </div>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => fetchData()}>Apply</Button>
        </div>
      </Card>

      {/* Summary cards */}
      {!loading && !error && Object.keys(groupedByIndicator).length > 0 && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {Object.entries(groupedByIndicator).map(([indName, items]) => {
            const latest = items[0];
            const prev = items[1];
            const change = latest && prev ? (latest.value - prev.value) : null;
            const color = INDICATOR_COLORS[indName] || "#6b7280";
            const isSelected = selectedIndicator === indName;

            return (
              <Card
                key={indName}
                className={`p-4 cursor-pointer transition hover:ring-2 hover:ring-primary/50 ${isSelected ? "ring-2 ring-primary" : ""}`}
                onClick={() => setSelectedIndicator(isSelected ? null : indName)}
              >
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-sm">{indName}</h3>
                  <div
                    className="w-3 h-3 rounded-full"
                    style={{ backgroundColor: color }}
                  />
                </div>
                <div className="text-2xl font-bold">
                  {latest?.value != null ? latest.value.toLocaleString(undefined, { maximumFractionDigits: 2 }) : "-"}
                </div>
                <div className="flex items-center gap-2 text-xs text-textSecondary mt-1">
                  <span>{latest?.date || "-"}</span>
                  {change !== null && (
                    <span className={change >= 0 ? "text-green-400" : "text-red-400"}>
                      {change >= 0 ? "▲" : "▼"} {Math.abs(change).toLocaleString(undefined, { maximumFractionDigits: 2 })}
                    </span>
                  )}
                </div>
                <div className="text-xs text-textSecondary mt-0.5">
                  {items.length} data points
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {/* Chart */}
      {selectedIndicator && chartData.length > 0 && (
        <Card className="p-4">
          <h2 className="text-lg font-medium mb-4">
            {selectedIndicator} — {currency}
          </h2>
          <div className="h-80">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={chartData} margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} domain={["auto", "auto"]} />
                <Tooltip />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="value"
                  name={selectedIndicator}
                  stroke={INDICATOR_COLORS[selectedIndicator] || "#6366f1"}
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      {/* Loading / Error */}
      {loading && (
        <Card className="p-6 text-center text-textSecondary">Loading...</Card>
      )}
      {error && !loading && (
        <Card className="p-6 text-center text-red-400">{error}</Card>
      )}
      {!loading && !error && data.length === 0 && (
        <Card className="p-6 text-center text-textSecondary">
          No indicator data found. Try adjusting the date range or running the FRED data fetch task.
        </Card>
      )}
    </div>
  );
}
